import { CollectionType } from '@/enums/collection-type';
import { PopularProviderPageExpName, PopularProviderPageExpVariants } from '.';
import { PopularTabType } from '@/interfaces/titles';
import { FilterCollection } from '@/stores/filter.store';
import {
	SnowplowClickoutContextGraphql,
	SnowplowExperimentContext,
	TrackingProviderPropertiesInterface,
} from '../../../helpers/tracking/providers';
import { SnowplowContext, SnowplowTitleContextGraphql } from '../../../helpers/tracking/providers/snowplow-contexts';
import { TrackingHelper } from '../../../helpers/tracking/tracking-helper';
import { getVm } from '../../../helpers/vm-helper';
import { Plan } from './interfaces/plan';
import axios from 'axios';
import { ObjectType, Offer } from '@/@types/graphql-types';
import { ProvidersShortNames, provider_links } from './hardcoded-data/provider-links';

interface TrackEventArgs {
	action: 'clickout' | 'impression' | 'click';
	label?: string;
	variant?: PopularProviderPageExpVariants;
	version?: string;
	contexts?: SnowplowContext[];
	property?: 'overview' | 'movies' | 'shows';
}

function getSelectedTab(routeName: string): PopularTabType | undefined {
	const name = routeName.split('app.titles.popular.provider')[1];
	if (name === undefined) {
		return undefined;
	}
	switch (name) {
		case '-movie':
			return PopularTabType.MOVIE;
		case '-show':
			return PopularTabType.SHOW;
		default:
			return PopularTabType.ALL;
	}
}

export function isExperimentActive(_routeName?: string, skipRouteCheck = false): boolean {
	const vm = getVm();
	const filterPopular: FilterCollection = vm?.$store.getters['filter/' + CollectionType.POPULAR];

	const isOneProviderSelected =
		filterPopular.providers.length === 1 && !!provider_links[filterPopular.providers[0] as ProvidersShortNames];

	const variant = vm?.$store.getters['experiment/activeVariantsWithControlGroup'][
		PopularProviderPageExpName
	] as PopularProviderPageExpVariants;

	const isLoggedIn: () => boolean = vm?.$store.getters['user/isLoggedIn'];

	const routeName: string = _routeName || vm?._route?.name;
	const checkIfProviderPage = skipRouteCheck ? true : routeName?.startsWith('app.titles.popular.provider');

	return checkIfProviderPage && !!variant && isOneProviderSelected && !isLoggedIn();
}

export function providerPageExpTrackEvent(
	{ version = '1', action, label, contexts = [], property }: TrackEventArgs,
	_routeName?: string,
	_selectedTab?: PopularTabType
) {
	const vm = getVm();

	if (!isExperimentActive(_routeName)) {
		return;
	}

	const variant = vm?.$store.getters['experiment/activeVariantsWithControlGroup'][
		PopularProviderPageExpName
	] as PopularProviderPageExpVariants;

	const routeName: string = _routeName || vm?._route?.name;
	const selectedTab = _selectedTab || getSelectedTab(routeName);

	const properties: TrackingProviderPropertiesInterface = { action, label, property: property ?? selectedTab };
	if (action === 'impression') properties.nonInteraction = true;

	const expContext = new SnowplowExperimentContext(PopularProviderPageExpName, version, variant);
	TrackingHelper.trackEvent('onpage_test', properties, [...contexts, expContext]);
}

export function trackWatchNowClick(watchNowOffer: Offer, objectId: number, objectType: ObjectType, moduleName: string) {
	const clickoutContext = SnowplowClickoutContextGraphql.fromProviderOffer(watchNowOffer as Offer);

	const titleContext = new SnowplowTitleContextGraphql(objectId, objectType);
	TrackingHelper.trackEvent(
		'clickout',
		{
			action: watchNowOffer?.monetizationType,
			label: watchNowOffer?.package.clearName,
			property: watchNowOffer?.presentationType,
		},
		[clickoutContext, titleContext]
	);

	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'watchnow_clicked',
			label: moduleName,
			property: watchNowOffer?.presentationType,
		},
		[clickoutContext, titleContext]
	);

	providerPageExpTrackEvent({
		action: 'click',
		label: 'watchnow_clicked',
	});
}

export function trackTitleClick(moduleName: string, useLastRouteName: boolean = true) {
	const vm = getVm();
	const routeName: string = useLastRouteName
		? vm.$store.state.routing?.lastRoute?.name
		: vm.$store.state.routing?.activeRoute?.name;

	const selectedTab = getSelectedTab(routeName);

	TrackingHelper.trackEvent('userinteraction', {
		action: 'title_clicked',
		label: moduleName,
		property: selectedTab,
	});
	providerPageExpTrackEvent(
		{
			label: 'title_clicked_modules',
			action: 'click',
		},
		routeName
	);
}

export function loadMicrosoftClarity() {
	const scriptId = 'microsoft-clarity';
	if (document.getElementById(scriptId)) return;

	const scriptText = `
                        (function(c,l,a,r,i,t,y){
                        c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
                        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
                        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
                        })(window, document, "clarity", "script", "ffbppisl6e");
                    `;

	const script = document.createElement('script');
	script.innerHTML = scriptText;
	script.id = scriptId;
	document.head.appendChild(script);
}

export async function loadProviderPlan(short_name: string) {
	try {
		const data = await axios.get<Plan[]>(`${ASSETS_DIR}/json_data/provider_plans/${short_name}.json`);
		return data.data;
	} catch (error) {}
	return [];
}
